import {
  resolveCloudAuthRedirectAction,
  type CloudAuthRedirectDecision,
} from "@cmmd-forge/client-runtime";

import type { CloudAuthFailureState } from "../rpc/wsConnectionState";
import type { RuntimeSurfaceState } from "../environments/runtimeSurface";

/**
 * WEB-mode CMMD auth expiry routing logic.
 *
 * The decision itself lives in the connection supervisor
 * (`resolveCloudAuthRedirectAction`), which owns re-auth prompting for every
 * surface. This adapter only translates web-specific inputs: which surfaces
 * can reach /login, and which paths are exempt.
 *
 * Three possible outcomes for a given (failureState, surface, pathname):
 *  - "navigate-login"  — failure is active, we are on a guarded path, web surface → go to /login
 *  - "clear-failure"   — we are already on /login and the atom is still set → clear it so
 *                        a fresh login doesn't immediately re-trigger the redirect
 *  - "none"            — nothing to do
 *
 * Only acts on web surfaces ("cloud-official" | "local-web-dev").
 * Desktop-cloud keeps its existing toast; desktop-local is unaffected.
 */
export type WebCloudAuthRedirectAction = CloudAuthRedirectDecision;

const WEB_SURFACES: ReadonlySet<RuntimeSurfaceState["surface"]> = new Set([
  "cloud-official",
  "local-web-dev",
]);

const AUTH_EXEMPT_PATHS: ReadonlySet<string> = new Set(["/login", "/pair"]);

export function resolveWebCloudAuthRedirectAction(input: {
  readonly failureState: CloudAuthFailureState;
  readonly surface: RuntimeSurfaceState["surface"];
  readonly pathname: string;
}): WebCloudAuthRedirectAction {
  return resolveCloudAuthRedirectAction({
    failureStatus: input.failureState.status,
    isWebSurface: WEB_SURFACES.has(input.surface),
    isAuthExemptPath: AUTH_EXEMPT_PATHS.has(input.pathname),
  });
}
